import type { BillingPlanLimitFieldKey } from '@/constants/billing.constant'
import { getRedisEnvPrefix } from '@/lib/redis/envPrefix'

export const PLAN_LIMIT_REACHED_CODE = 'PLAN_LIMIT_REACHED' as const

export const STOCK_USAGE_KEYS = [
    'maxUsers',
    'maxActiveJobs',
    'maxJobsTotal',
    'maxJobDescriptions',
    'maxAssessments',
    'maxScreeningInterviewsTotal',
] as const satisfies readonly BillingPlanLimitFieldKey[]

export const MONTHLY_USAGE_KEYS = [
    'maxScreeningInterviewsPerMonth',
    'maxHumanInterviewsPerMonth',
    'maxResumeParsesPerMonth',
] as const satisfies readonly BillingPlanLimitFieldKey[]

export type StockUsageKey = (typeof STOCK_USAGE_KEYS)[number]
export type MonthlyUsageKey = (typeof MONTHLY_USAGE_KEYS)[number]

export function isMonthlyUsageKey(key: BillingPlanLimitFieldKey): key is MonthlyUsageKey {
    return (MONTHLY_USAGE_KEYS as readonly string[]).includes(key)
}

export function isStockUsageKey(key: BillingPlanLimitFieldKey): key is StockUsageKey {
    return (STOCK_USAGE_KEYS as readonly string[]).includes(key)
}

export function planLimitReachedMessage(key: BillingPlanLimitFieldKey): string {
    return `Plan limit reached for ${key}`
}

/** `{env}:billing:limits:{companyId}` */
export function billingLimitsRedisKey(companyId: number): string {
    return `${getRedisEnvPrefix()}:billing:limits:${companyId}`
}

/** `{env}:billing:usage:{companyId}` — stock counters */
export function billingUsageRedisKey(companyId: number): string {
    return `${getRedisEnvPrefix()}:billing:usage:${companyId}`
}

/** `{env}:billing:usage:{companyId}:{periodKey}` — monthly meters */
export function billingPeriodUsageRedisKey(companyId: number, periodKey: string): string {
    return `${getRedisEnvPrefix()}:billing:usage:${companyId}:${periodKey}`
}

/**
 * Calendar month UTC window — used for resume-parse capacity grants and overage.
 */
export function resolveCalendarMonthPeriodKey(now: Date = new Date()): {
    periodKey: string
    periodStart: Date
    periodEnd: Date
} {
    const y = now.getUTCFullYear()
    const m = now.getUTCMonth()
    const periodStart = new Date(Date.UTC(y, m, 1, 0, 0, 0, 0))
    const periodEnd = new Date(Date.UTC(y, m + 1, 1, 0, 0, 0, 0))
    const periodKey = `${y}-${String(m + 1).padStart(2, '0')}`
    return { periodKey, periodStart, periodEnd }
}

/**
 * Billing period key for monthly meters.
 * Prefer Stripe window: [currentPeriodEnd - 1 month, currentPeriodEnd).
 * Else calendar month UTC: `YYYY-MM`.
 */
export function resolveBillingPeriodKey(
    currentPeriodEnd: Date | null | undefined,
    now: Date = new Date()
): {
    periodKey: string
    periodStart: Date
    periodEnd: Date
} {
    if (currentPeriodEnd != null && !Number.isNaN(currentPeriodEnd.getTime())) {
        const periodEnd = new Date(currentPeriodEnd)
        const periodStart = new Date(periodEnd)
        periodStart.setUTCMonth(periodStart.getUTCMonth() - 1)
        const periodKey = `pe-${periodEnd.toISOString().slice(0, 10)}`
        return { periodKey, periodStart, periodEnd }
    }

    return resolveCalendarMonthPeriodKey(now)
}
